import { useState } from 'react';

import { type WriteContractReturnType } from '@wagmi/core';
import { routerAbi } from 'shared/lib/abis/Router';
import { FilledStylizedButton } from 'shared/lib/components/common/Buttons';
import TokenAmountInput from 'shared/lib/components/common/TokenAmountInput';
import { Text } from 'shared/lib/components/common/Typography';
import { isHealthy } from 'shared/lib/data/BalanceSheet';
import { Liabilities } from 'shared/lib/data/Borrower';
import { STACKING_SALMON_ROUTER_ADDRESS } from 'shared/lib/data/constants/ChainSpecific';
import { TERMS_OF_SERVICE_URL } from 'shared/lib/data/constants/Values';
import { GN, GNFormat } from 'shared/lib/data/GoodNumber';
import { Token } from 'shared/lib/data/Token';
import useChain from 'shared/lib/hooks/UseChain';
import { Permit2State, usePermit2 } from 'shared/lib/hooks/UsePermit2';
import { Address, Chain } from 'viem';
import { useAccount, useBalance, useSimulateContract, useWriteContract } from 'wagmi';

import { BorrowerNftBorrower } from '../../../../hooks/useDeprecatedMarginAccountShim';
import HealthBar from '../../../common/HealthBar';

const SECONDARY_COLOR = '#EC5D2A';
const TERTIARY_COLOR = '#FFFFFF';

enum ConfirmButtonState {
  INSUFFICIENT_FUNDS,
  PERMIT_ASSET,
  APPROVE_ASSET,
  REPAYING_TOO_MUCH,
  WAITING_FOR_USER,
  WAITING_FOR_TRANSACTION,
  READY,
  LOADING,
  DISABLED,
}

const permit2StateToButtonStateMap = {
  [Permit2State.ASKING_USER_TO_APPROVE]: ConfirmButtonState.WAITING_FOR_USER,
  [Permit2State.ASKING_USER_TO_SIGN]: ConfirmButtonState.WAITING_FOR_USER,
  [Permit2State.DONE]: undefined,
  [Permit2State.FETCHING_DATA]: ConfirmButtonState.LOADING,
  [Permit2State.READY_TO_APPROVE]: ConfirmButtonState.APPROVE_ASSET,
  [Permit2State.READY_TO_SIGN]: ConfirmButtonState.PERMIT_ASSET,
  [Permit2State.WAITING_FOR_TRANSACTION]: ConfirmButtonState.WAITING_FOR_TRANSACTION,
};

function getConfirmButton(state: ConfirmButtonState, token: Token): { text: string; enabled: boolean } {
  switch (state) {
    case ConfirmButtonState.INSUFFICIENT_FUNDS:
      return { text: `Insufficient ${token.symbol}`, enabled: false };
    case ConfirmButtonState.REPAYING_TOO_MUCH:
      return { text: 'Repaying too much', enabled: false };
    case ConfirmButtonState.PERMIT_ASSET:
      return { text: `Permit ${token.symbol}`, enabled: true };
    case ConfirmButtonState.APPROVE_ASSET:
      return { text: `Approve ${token.symbol}`, enabled: true };
    case ConfirmButtonState.WAITING_FOR_TRANSACTION:
      return { text: 'Pending', enabled: false };
    case ConfirmButtonState.WAITING_FOR_USER:
      return { text: 'Check Wallet', enabled: false };
    case ConfirmButtonState.READY:
      return { text: 'Confirm', enabled: true };
    case ConfirmButtonState.LOADING:
      return { text: 'Loading...', enabled: false };
    case ConfirmButtonState.DISABLED:
    default:
      return { text: 'Confirm', enabled: false };
  }
}

type ConfirmButtonProps = {
  activeChain: Chain;
  borrower: BorrowerNftBorrower;
  userAddress: Address;
  shouldRepayMax: boolean;
  isRepayingToken0: boolean;
  existingLiabilityGN: GN;
  repayAmount: GN;
  repayToken: Token;
  repayTokenBalance: GN;
  setIsOpen: (isOpen: boolean) => void;
  setPendingTxn: (pendingTxn: WriteContractReturnType | null) => void;
};

function ConfirmButton(props: ConfirmButtonProps) {
  const {
    activeChain,
    shouldRepayMax,
    repayAmount: repayAmountSpecified,
    repayTokenBalance,
    existingLiabilityGN,
    borrower,
    repayToken,
    isRepayingToken0,
    userAddress,
    setIsOpen,
    setPendingTxn,
  } = props;
  const repayAmount = shouldRepayMax ? repayAmountSpecified.recklessMul(1.005) : repayAmountSpecified;

  const [isPending, setIsPending] = useState(false);

  const {
    state: permit2State,
    action: permit2Action,
    result: permit2Result,
  } = usePermit2(activeChain, repayToken, userAddress, STACKING_SALMON_ROUTER_ADDRESS[activeChain.id], repayAmount);

  const { data: repayWithPermit2Config, refetch: refetchRepayWithPermit2 } = useSimulateContract({
    address: STACKING_SALMON_ROUTER_ADDRESS[activeChain.id],
    abi: routerAbi,
    functionName: 'repayWithPermit2',
    args: [
      isRepayingToken0 ? borrower.lender0 : borrower.lender1,
      shouldRepayMax,
      permit2Result.amount.toBigInt(),
      borrower.address,
      BigInt(permit2Result.nonce ?? 0n),
      BigInt(permit2Result.deadline),
      permit2Result.signature ?? '0x',
    ],
    chainId: activeChain.id,
    query: { enabled: permit2State === Permit2State.DONE },
  });
  const { writeContractAsync: repayWithPermit2 } = useWriteContract();

  // MARK: Determining button state -----------------------------------------------------------------------------------
  let confirmButtonState: ConfirmButtonState;
  if (isPending) {
    confirmButtonState = ConfirmButtonState.WAITING_FOR_TRANSACTION;
  } else if (repayAmount.isZero()) {
    confirmButtonState = ConfirmButtonState.DISABLED;
  } else if (repayAmount.gt(repayTokenBalance)) {
    confirmButtonState = ConfirmButtonState.INSUFFICIENT_FUNDS;
  } else if (repayAmountSpecified.gt(existingLiabilityGN)) {
    confirmButtonState = ConfirmButtonState.REPAYING_TOO_MUCH;
  } else {
    confirmButtonState = permit2StateToButtonStateMap[permit2State] ?? ConfirmButtonState.READY;
  }

  // MARK: Get the button itself --------------------------------------------------------------------------------------
  // --> UI
  const confirmButton = getConfirmButton(confirmButtonState, repayToken);
  // --> action
  const confirmButtonAction = () => {
    if (permit2Action) {
      permit2Action();
      return;
    }

    if (confirmButtonState === ConfirmButtonState.READY) {
      if (!repayWithPermit2Config) {
        refetchRepayWithPermit2();
        return;
      }
      setIsPending(true);
      repayWithPermit2(repayWithPermit2Config.request)
        .then((hash) => {
          setPendingTxn(hash);
          setIsOpen(false);
        })
        .catch((e) => console.error(e))
        .finally(() => {
          setIsPending(false);
        });
    }
  };

  return (
    <FilledStylizedButton size='M' fillWidth={true} disabled={!confirmButton.enabled} onClick={confirmButtonAction}>
      {confirmButton.text}
    </FilledStylizedButton>
  );
}

export type RepayModalContentProps = {
  borrower: BorrowerNftBorrower;
  setIsOpen: (isOpen: boolean) => void;
  setPendingTxnResult: (result: WriteContractReturnType | null) => void;
};

export default function RepayModalContent(props: RepayModalContentProps) {
  const { borrower, setIsOpen, setPendingTxnResult } = props;

  const [repayAmountStr, setRepayAmountStr] = useState('');

  const { address: userAddress } = useAccount();
  const activeChain = useChain();

  // TODO: This assumes that only one token is borrowed and one token is collateralized
  const isRepayingToken0 = borrower.liabilities.amount0 > 0;

  // TODO: This logic needs to change once we support more complex borrowing
  const repayToken = isRepayingToken0 ? borrower.token0 : borrower.token1;

  const { data: tokenBalanceData } = useBalance({
    address: userAddress,
    chainId: activeChain.id,
    token: repayToken.address,
    query: { enabled: userAddress !== undefined },
  });
  const tokenBalance = GN.fromDecimalString(tokenBalanceData?.formatted ?? '0', repayToken.decimals);

  // TODO: This assumes that the borrowing token is always the opposite of the collateral token
  // and that only one token is borrowed and one token is collateralized
  const numericExistingLiability = isRepayingToken0 ? borrower.liabilities.amount0 : borrower.liabilities.amount1;
  const existingLiability = GN.fromNumber(numericExistingLiability, repayToken.decimals);
  const repayAmount = GN.fromDecimalString(repayAmountStr || '0', repayToken.decimals);
  const newLiability = existingLiability.sub(repayAmount);

  const maxRepayStr = GN.min(existingLiability, tokenBalance).toString(GNFormat.DECIMAL);
  // NOTE: Don't just use `repayAmountStr === maxRepayStr` because the max repay flow will fail
  // if `tokenBalance` is the constraint.
  const shouldRepayMax = repayAmountStr === existingLiability.toString(GNFormat.DECIMAL);

  // TODO: use GN
  const newLiabilities: Liabilities = {
    amount0: isRepayingToken0 ? newLiability.toNumber() : borrower.liabilities.amount0,
    amount1: isRepayingToken0 ? borrower.liabilities.amount1 : newLiability.toNumber(),
  };

  const { health: newHealth } = isHealthy(
    borrower.assets,
    newLiabilities,
    borrower.sqrtPriceX96,
    borrower.iv,
    borrower.nSigma,
    borrower.token0.decimals,
    borrower.token1.decimals
  );

  return (
    <>
      <div className='flex justify-between items-center mb-4'>
        <TokenAmountInput
          token={repayToken}
          onChange={(updatedAmount: string) => {
            setRepayAmountStr(updatedAmount);
          }}
          value={repayAmountStr}
          max={maxRepayStr}
          maxed={shouldRepayMax}
        />
      </div>
      <div className='flex flex-col gap-1 w-full'>
        <Text size='M' weight='bold'>
          Summary
        </Text>
        <Text size='XS' color={SECONDARY_COLOR} className='overflow-hidden text-ellipsis'>
          You're repaying{' '}
          <strong>
            {repayAmountStr || '0.00'} {repayToken.symbol}
          </strong>{' '}
          using this{' '}
          <strong>
            {borrower.token0.symbol}/{borrower.token1.symbol}
          </strong>{' '}
          smart wallet. Your total borrows for this token in this smart wallet will be{' '}
          <strong>
            {newLiability.toString(GNFormat.DECIMAL)} {repayToken.symbol}
          </strong>
          .
        </Text>
        <div className='mt-2'>
          <HealthBar health={newHealth} />
        </div>
      </div>
      <div className='w-full ml-auto mt-8'>
        <ConfirmButton
          activeChain={activeChain}
          borrower={borrower}
          userAddress={userAddress ?? '0x'}
          existingLiabilityGN={existingLiability}
          shouldRepayMax={shouldRepayMax}
          isRepayingToken0={isRepayingToken0}
          repayAmount={repayAmount}
          repayToken={repayToken}
          repayTokenBalance={tokenBalance}
          setIsOpen={setIsOpen}
          setPendingTxn={setPendingTxnResult}
        />
      </div>
      <Text size='XS' color={TERTIARY_COLOR} className='w-full mt-2'>
        By repaying, you agree to our{' '}
        <a href={TERMS_OF_SERVICE_URL} className='underline' rel='noreferrer' target='_blank'>
          Terms of Service
        </a>{' '}
        and acknowledge that you may lose your money. Stacking Salmon is not responsible for any losses you may incur.
        It is your duty to educate yourself and be aware of the risks.
      </Text>
    </>
  );
}
